import CloseIcon from "@mui/icons-material/Close";
import StarsIcon from "@mui/icons-material/Stars";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography
} from "@mui/material";
import React, { useState } from "react";

interface RewardCoinsChipProps {
  coins: number;
  punchShiftLimit?: number;
}

export const RewardCoinsChip: React.FC<RewardCoinsChipProps> = ({
  coins,
  punchShiftLimit = 15
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Range: 0-160 coins → 0-100% della barra
  const maxRange = 140; // range massimo per la visualizzazione
  const barPercentage = Math.min(Math.max((coins / maxRange) * 100, 0), 100);

  // Determina il colore della barra in base al punteggio
  const getBarColor = () => {
    if (coins >= 70) return "#4caf50"; // Verde
    if (coins >= 40) return "#FFD700"; // Giallo
    return "#f44336"; // Rosso
  };

  return (
    <>
      <Chip
        icon={<StarsIcon />}
        label={coins}
        variant="filled"
        size="medium"
        onClick={handleOpen}
        sx={{
          borderColor: "#FFD700",
          backgroundColor: "rgba(255,225,53,0.18)",
          color: "#FFD700",
          fontSize: "16px",
          fontWeight: "bold",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "rgba(255,225,53,0.28)",
            transform: "scale(1.05)"
          },
          "& .MuiChip-icon": {
            color: "#FFD700"
          }
        }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <StarsIcon sx={{ color: "#FFD700", fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Il tuo punteggio
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} py={1}>
            {/* Punteggio attuale */}
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                borderRadius: 2,
                background:
                  "linear-gradient(135deg, rgba(255,225,53,0.1) 0%, rgba(255,193,7,0.1) 100%)"
              }}
            >
              <Typography variant="h3" fontWeight="bold" color="#FFD700">
                {coins}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Punteggio attuale
              </Typography>
            </Box>

            <Divider />

            {/* Barra di progresso */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                📈 Progresso
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  mt: 1
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <LinearProgress
                    variant="determinate"
                    value={barPercentage}
                    sx={{
                      height: 12,
                      borderRadius: 1,
                      bgcolor: "rgba(0,0,0,0.1)",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: getBarColor(),
                        borderRadius: 1,
                        transition: "all 0.3s ease"
                      }
                    }}
                  />
                </Box>

                <Stack direction="row" justifyContent="space-between" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    0
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {maxRange}
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <Divider />

            {/* Benefici attuali */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                🎁 Benefici Attuali
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  mt: 1
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    Limite modifica timbrature
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {punchShiftLimit} minuti
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mt={1}
                  display="block"
                >
                  Puoi modificare le tue timbrature fino a {punchShiftLimit}{" "}
                  minuti dall'orario corrente
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RewardCoinsChip;
